import SearchIcon from '@mui/icons-material/Search';
import { Box, InputAdornment, ListItemText, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useDebouncedCallback from '../../../../../hooks/useDebouncedCallback';
import { IDepartment } from '../../../../../types/organizations';
import { ArrowDirectionEnum, KeyboardKeysEnum } from '../../../../../types/shell';
import {
  ClearIconWrapper,
  FilterWrapper,
  getControlMenuItemStyles,
  getSelectStyles,
  HoverOverlay,
  paperPropsStyles,
  scrollableListStyles,
  SearchField,
  searchFieldWrapperStyles,
  StyledClearIcon,
  StyledSelect,
} from './DepartmentFilter.styles';
import { DepartmentMenuItem } from './DepartmentMenuItem';
import { DisplayLabel } from './DisplayLabel';
import { SelectAllControl } from './SelectAllControl';

interface IProps {
  value: string[];
  onChange: (value: string[]) => void;
  departments: IDepartment[];
  dataAction?: string;
}

const SEARCH_INPUT_INDEX = -1;
const FIRST_ITEM_INDEX = 0;

export const DepartmentFilter: React.FC<IProps> = ({
  value = [],
  onChange,
  departments,
  dataAction = 'department_filter',
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [localValue, setLocalValue] = useState<string[]>(value);
  const [focusedIndex, setFocusedIndex] = useState<number>(SEARCH_INPUT_INDEX);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuItemRefs = useRef<Map<number, HTMLLIElement>>(new Map());

  const filteredDepartments = useMemo(() => {
    if (!searchQuery.trim()) {
      return departments;
    }
    const query = searchQuery.toLowerCase();
    return departments.filter((dept) => dept.name.toLowerCase().includes(query));
  }, [departments, searchQuery]);

  const allSelected = useMemo(() => {
    return filteredDepartments.length > 0 && filteredDepartments.every((dept) => localValue.includes(dept.uid));
  }, [filteredDepartments, localValue]);

  const indeterminate = useMemo(() => {
    return filteredDepartments.some((dept) => localValue.includes(dept.uid)) && !allSelected;
  }, [filteredDepartments, localValue, allSelected]);

  const hasValue = useMemo(() => value.length > 0, [value]);

  const totalItems = useMemo(() => filteredDepartments.length + 1, [filteredDepartments.length]);

  const selectStyles = useMemo(() => getSelectStyles(hasValue), [hasValue]);

  // Debounced onChange to trigger backend request after 1 second
  const debouncedOnChange = useDebouncedCallback((newValue: string[]) => {
    onChange(newValue);
  }, 1000);

  const isControlItemIndex = useCallback(
    (departmentIndex: number): boolean => {
      return departmentIndex === totalItems - 1;
    },
    [totalItems],
  );

  const handleSelectAll = useCallback(() => {
    const filteredDeptIds = filteredDepartments.map((dept) => dept.uid);
    const newValue = Array.from(new Set([...localValue, ...filteredDeptIds]));

    setLocalValue(newValue);
    debouncedOnChange(newValue);
  }, [filteredDepartments, localValue, debouncedOnChange]);

  const handleDeselectAll = useCallback(() => {
    const filteredDeptIds = filteredDepartments.map((dept) => dept.uid);
    const newValue = localValue.filter((id) => !filteredDeptIds.includes(id));
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  }, [filteredDepartments, localValue, debouncedOnChange]);

  const toggleControlItem = useCallback(() => {
    if (indeterminate || !allSelected) {
      handleSelectAll();
    } else {
      handleDeselectAll();
    }
  }, [indeterminate, allSelected, handleSelectAll, handleDeselectAll]);

  const toggleSingleDepartment = useCallback(
    (department: IDepartment) => {
      const isSelected = localValue.includes(department.uid);
      const newValue = isSelected ? localValue.filter((id) => id !== department.uid) : [...localValue, department.uid];

      setLocalValue(newValue);
      debouncedOnChange(newValue);
    },
    [localValue, debouncedOnChange],
  );

  const toggleDepartmentSelection = useCallback(
    (departmentIndex: number) => {
      if (isControlItemIndex(departmentIndex)) {
        toggleControlItem();
      } else {
        const department = filteredDepartments[departmentIndex];
        if (department) {
          toggleSingleDepartment(department);
        }
      }
    },
    [filteredDepartments, isControlItemIndex, toggleControlItem, toggleSingleDepartment],
  );

  const handleEscapeKey = useCallback(() => {
    setOpen(false);
    setSearchQuery('');
    setFocusedIndex(SEARCH_INPUT_INDEX);
  }, []);

  const handleTabKey = useCallback(
    (shiftPressed: boolean) => {
      if (focusedIndex === SEARCH_INPUT_INDEX) {
        setFocusedIndex(FIRST_ITEM_INDEX);
      } else if (shiftPressed) {
        if (focusedIndex > FIRST_ITEM_INDEX) {
          setFocusedIndex(focusedIndex - 1);
        } else {
          setFocusedIndex(SEARCH_INPUT_INDEX);
          searchInputRef.current?.focus();
        }
      } else {
        if (focusedIndex < totalItems - 1) {
          setFocusedIndex(focusedIndex + 1);
        }
      }
    },
    [focusedIndex, totalItems],
  );

  const handleArrowKey = useCallback(
    (direction: ArrowDirectionEnum) => {
      if (direction === ArrowDirectionEnum.Down) {
        if (focusedIndex === SEARCH_INPUT_INDEX) {
          setFocusedIndex(FIRST_ITEM_INDEX);
        } else if (focusedIndex < totalItems - 1) {
          setFocusedIndex(focusedIndex + 1);
        }
      } else {
        if (focusedIndex > FIRST_ITEM_INDEX) {
          setFocusedIndex(focusedIndex - 1);
        } else if (focusedIndex === FIRST_ITEM_INDEX) {
          setFocusedIndex(SEARCH_INPUT_INDEX);
          searchInputRef.current?.focus();
        }
      }
    },
    [focusedIndex, totalItems],
  );

  const handleSelectKey = useCallback(() => {
    if (focusedIndex !== SEARCH_INPUT_INDEX) {
      toggleDepartmentSelection(focusedIndex);
    }
  }, [focusedIndex, toggleDepartmentSelection]);

  const preventDefaultAndStopPropagation = useCallback((event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleMenuKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case KeyboardKeysEnum.Escape:
          preventDefaultAndStopPropagation(event);
          handleEscapeKey();
          break;

        case KeyboardKeysEnum.Tab:
          event.preventDefault();
          handleTabKey(event.shiftKey);
          break;

        case KeyboardKeysEnum.ArrowDown:
          preventDefaultAndStopPropagation(event);
          handleArrowKey(ArrowDirectionEnum.Down);
          break;

        case KeyboardKeysEnum.ArrowUp:
          preventDefaultAndStopPropagation(event);
          handleArrowKey(ArrowDirectionEnum.Up);
          break;

        case KeyboardKeysEnum.Space:
          if (focusedIndex !== SEARCH_INPUT_INDEX) {
            preventDefaultAndStopPropagation(event);
            handleSelectKey();
          }
          break;
        case KeyboardKeysEnum.Enter:
          preventDefaultAndStopPropagation(event);
          handleSelectKey();
          break;

        default:
          break;
      }
    },
    [handleEscapeKey, handleTabKey, handleArrowKey, handleSelectKey, focusedIndex, preventDefaultAndStopPropagation],
  );

  const handleCheckboxClick = useCallback(
    (event: React.MouseEvent) => {
      preventDefaultAndStopPropagation(event);
      toggleControlItem();
    },
    [toggleControlItem, preventDefaultAndStopPropagation],
  );

  const handleClear = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      debouncedOnChange.cancel();

      setLocalValue([]);
      onChange([]);

      setOpen(false);
    },
    [onChange, debouncedOnChange],
  );

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleDepartmentClick = useCallback(
    (departmentUid: string, isSelected: boolean) => (e: React.MouseEvent) => {
      if (e.detail === 0) {
        return;
      }

      preventDefaultAndStopPropagation(e);

      const newValue = isSelected ? localValue.filter((id) => id !== departmentUid) : [...localValue, departmentUid];

      setLocalValue(newValue);
      debouncedOnChange(newValue);
    },
    [localValue, debouncedOnChange, preventDefaultAndStopPropagation],
  );

  const handleControlClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.detail === 0) {
        return;
      }
      handleCheckboxClick(e);
    },
    [handleCheckboxClick],
  );

  const handleMenuItemRef = useCallback((index: number, el: HTMLLIElement | null) => {
    if (el) {
      menuItemRefs.current.set(index, el);
    } else {
      menuItemRefs.current.delete(index);
    }
  }, []);

  const renderDisplayLabel = useCallback(() => {
    const selectedDepartments = departments.filter((dept) => value.includes(dept.uid)).map((dept) => dept.name);

    return <DisplayLabel selectedDepartments={selectedDepartments} label="Department" />;
  }, [value, departments]);

  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  useEffect(() => {
    setLocalValue(value);

    if (!open) {
      debouncedOnChange.flush();
      setFocusedIndex(SEARCH_INPUT_INDEX);
    }
  }, [open, value, debouncedOnChange]);

  useEffect(() => {
    if (focusedIndex >= totalItems) {
      setFocusedIndex(SEARCH_INPUT_INDEX);
    }
  }, [totalItems, focusedIndex]);

  useEffect(() => {
    if (focusedIndex !== SEARCH_INPUT_INDEX) {
      const focusedElement = menuItemRefs.current.get(focusedIndex);
      if (focusedElement) {
        focusedElement.focus();
        focusedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [focusedIndex]);

  return (
    <FilterWrapper>
      <HoverOverlay className="hover-overlay" />
      <StyledSelect
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setSearchQuery('');
        }}
        multiple
        value={localValue}
        onChange={(e) => e.stopPropagation()}
        displayEmpty
        renderValue={renderDisplayLabel}
        disableUnderline
        variant="standard"
        MenuProps={{
          autoFocus: false,
          disableAutoFocusItem: true,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          PaperProps: {
            sx: paperPropsStyles,
          },
          MenuListProps: {
            onKeyDown: (e) => {
              if (
                [KeyboardKeysEnum.Space, KeyboardKeysEnum.Enter, KeyboardKeysEnum.Tab].includes(
                  e.key as KeyboardKeysEnum,
                )
              ) {
                e.stopPropagation();
              }
            },
          },
        }}
        sx={selectStyles}
        data-action={dataAction}
      >
        <Box sx={searchFieldWrapperStyles}>
          <SearchField
            inputRef={searchInputRef}
            placeholder="Search"
            value={searchQuery}
            onChange={handleSearchChange}
            fullWidth
            variant="outlined"
            size="small"
            autoFocus
            onKeyDown={handleMenuKeyDown}
            onClick={(e) => {
              e.stopPropagation();
            }}
            data-action="department_filter_search"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ fontSize: 24, color: 'action.active' }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Box sx={scrollableListStyles}>
          {filteredDepartments.length === 0 ? (
            <ListItemText primary="No departments found" sx={{ paddingLeft: 2 }} />
          ) : (
            filteredDepartments.map((department, index) => {
              const isSelected = localValue.includes(department.uid);
              const isFocused = focusedIndex === index;
              return (
                <DepartmentMenuItem
                  key={department.uid}
                  department={department}
                  index={index}
                  isSelected={isSelected}
                  isFocused={isFocused}
                  onItemClick={handleDepartmentClick}
                  onKeyDown={handleMenuKeyDown}
                  onRef={handleMenuItemRef}
                />
              );
            })
          )}
        </Box>
        <SelectAllControl
          allSelected={allSelected}
          indeterminate={indeterminate}
          isFocused={focusedIndex === filteredDepartments.length}
          onClick={handleControlClick}
          onKeyDown={handleMenuKeyDown}
          onRef={(el) => handleMenuItemRef(filteredDepartments.length, el)}
          controlMenuItemStyles={() => getControlMenuItemStyles(theme)}
        />
      </StyledSelect>
      {hasValue && (
        <ClearIconWrapper onClick={handleClear} data-action="department_filter_clear_btn">
          <StyledClearIcon />
        </ClearIconWrapper>
      )}
    </FilterWrapper>
  );
};
